import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import type { Club } from '@court-sync/shared';
import { Button, Switch } from '@/components/ui';
import { clubService } from '@/services/club';

interface ClubCourtHireSettingsTabProps {
  club: Club;
  onSaved: (club: Club) => void;
}

interface CourtHireSettingsForm {
  guestCheckoutEnabled: boolean;
}

export function ClubCourtHireSettingsTab({ club, onSaved }: ClubCourtHireSettingsTabProps) {
  const { control, handleSubmit, reset, formState: { isSubmitting } } = useForm<CourtHireSettingsForm>({
    defaultValues: { guestCheckoutEnabled: club.guestCheckoutEnabled ?? false },
  });

  useEffect(() => {
    reset({ guestCheckoutEnabled: club.guestCheckoutEnabled ?? false });
  }, [club, reset]);

  const onSubmit = async (values: CourtHireSettingsForm) => {
    const res = await clubService.updateClub(club.id, {
      guestCheckoutEnabled: values.guestCheckoutEnabled,
    });
    if (res.success && res.data) {
      toast.success('Court hire settings updated');
      onSaved(res.data);
    } else {
      toast.error(res.error || 'Failed to update settings');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-xl">
      <div className="flex items-start gap-3">
        <Controller
          name="guestCheckoutEnabled"
          control={control}
          render={({ field }) => (
            <Switch
              id="guest-checkout-enabled"
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
        <label htmlFor="guest-checkout-enabled" className="text-sm cursor-pointer">
          <span className="font-medium text-gray-900 dark:text-white">Allow guest court hire checkout</span>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Visitors can book courts without an account using name, email, and card payment on your
            public court hire page.
          </p>
        </label>
      </div>
      <Button type="submit" isLoading={isSubmitting}>Save court hire settings</Button>
    </form>
  );
}
